import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { post } from "../lib/api";
import { ErrorNote } from "../components/ui";

export function LoginPage() {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/api/session/login", { email, password });
      await qc.invalidateQueries({ queryKey: ["session"] });
      // Only ever continue to a page of ONE itself (no open redirect to other sites).
      const next = new URLSearchParams(window.location.search).get("next") ?? "/";
      window.location.href = next.startsWith("/") && !next.startsWith("//") && !next.startsWith("/\\") ? next : "/";
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="brand-bg flex min-h-full flex-col items-center justify-center gap-6 p-4">
      <form onSubmit={submit} className="w-full max-w-[400px] space-y-5 rounded-2xl bg-white p-8 shadow-[0_24px_64px_-16px_rgb(0_0_0/0.55)]">
        <div className="space-y-3 text-center">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl border border-[var(--line)] bg-white shadow-sm">
            <img src="/brand/mark.png" alt="Infinite Fusion" className="h-11 w-auto" />
          </span>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight">Infinite Fusion ONE</h1>
            <p className="muted text-sm">Infinite Fusion · Sign in to continue</p>
          </div>
        </div>
        <ErrorNote error={error} />
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Email</span>
          <input className="input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Password</span>
          <input className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <button className="btn btn-primary w-full justify-center" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <div className="muted flex items-center gap-3 text-xs"><span className="line h-px flex-1 border-t" />or<span className="line h-px flex-1 border-t" /></div>
        <a className="btn w-full justify-center" href={`https://auth.infinite-fusion.com/api/oauth/microsoft/start?redirect_to=${encodeURIComponent(`${window.location.origin}/`)}`}>
          <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true"><rect width="10" height="10" fill="#f25022" /><rect x="11" width="10" height="10" fill="#7fba00" /><rect y="11" width="10" height="10" fill="#00a4ef" /><rect x="11" y="11" width="10" height="10" fill="#ffb900" /></svg>
          Sign in with Microsoft
        </a>
        <p className="muted text-center text-xs">Same login as every other Infinite Fusion app.</p>
      </form>
      <p className="text-center text-xs text-white/40">Access restricted to authorised personnel</p>
    </div>
  );
}

export function NoAccessPage({ name }: { name?: string }) {
  return (
    <div className="brand-bg flex min-h-full items-center justify-center p-4">
      <div className="panel max-w-md space-y-3 p-7 text-center shadow-2xl shadow-black/40">
        <img src="/brand/mark.png" alt="Infinite Fusion" className="mx-auto h-12 w-auto" />
        <h1 className="text-lg font-semibold">Welcome{name ? `, ${name}` : ""}</h1>
        <p className="muted text-sm">
          You're signed in, but you don't have a role in ONE yet. An administrator can grant you access from Admin → People.
        </p>
        <button
          className="btn"
          onClick={async () => {
            await post("/api/session/logout", {});
            window.location.href = "/login";
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
